import { ComponentProps, forwardRef, useState } from "react";

import { deserializeHtmlToRichTextDocument, serializeRichTextDocumentToHtml } from "./editor/htmlSerializer";
import RichText, { RichTextDocument } from "./RichText";

type Props = Omit<ComponentProps<typeof RichText>, "value" | "onChange"> & {
    value: string;
    onChange: (value: string) => void;
};

const createState = (html: string, version: number) => {
    const document = deserializeHtmlToRichTextDocument(html);
    return { html, document, serialized: serializeRichTextDocumentToHtml(document), version };
};

/** Keep untouched API HTML verbatim; serialize only when the editor document changes. */
export const HtmlRichText = forwardRef<HTMLDivElement, Props>(({ value, onChange, ...props }, ref) => {
    const [state, setState] = useState(() => createState(value, 0));
    if (value !== state.html) {
        setState(createState(value, state.version + 1));
    }

    const handleChange = (document: RichTextDocument) => {
        const html = serializeRichTextDocumentToHtml(document);
        if (html === state.serialized) {
            return;
        }
        setState({ ...state, html, serialized: html });
        onChange(html);
    };

    return <RichText {...props} key={state.version} ref={ref} value={state.document} onChange={handleChange} />;
});

HtmlRichText.displayName = "HtmlRichText";
